/**
 * The Payment label on the admin Sessions list and Session detail
 * (2026-10-22).
 *
 * The list said "Paid" whenever stripe_payment_intent_id was set. The
 * payment_intent.canceled webhook stamps that id too, so a start the charger
 * refused or never answered, whose pre-auth hold was cancelled, read
 * "Payment: Paid" next to $0.00. Warren's ruling: a session that never
 * started carries no charge and says so, "No charge", or "Released" when a
 * pre-auth hold was placed and let go.
 *
 * "Never started" is the transaction id, not the status: StopTransaction
 * also writes 'failed' for a real session that lost power, and that one can
 * bill. A shell the charger never promoted keeps its 'pending-' placeholder
 * (or the legacy '' default); the promotion replaces it with the charger's
 * id. A session that cost nothing is in the same class: there is no money
 * to call paid. Anything with evidence of collected money keeps the old
 * rule, so a real paid session reads exactly as before.
 */

export type SessionPaymentState =
  | 'live' | 'paid' | 'pending' | 'no_charge' | 'released'
  | 'not_collected' | 'waived' | 'waived_by_evi' | 'partly_paid' | 'failed'
  | 'v1_paid' | 'v1_failed' | 'v1_pending' | 'v1_other' | 'v1_collected';

export const SESSION_PAYMENT_LABELS: Record<Exclude<SessionPaymentState, 'live'>, string> = {
  paid: 'Paid',
  pending: 'Pending',
  no_charge: 'No charge',
  released: 'Released',
  not_collected: 'Not collected',
  waived: 'Waived (under 50¢)',
  waived_by_evi: 'Waived by EVI',
  partly_paid: 'Partly paid',
  failed: 'Payment failed',
  v1_paid: 'Paid on v1',
  v1_failed: 'Failed on v1',
  v1_pending: 'Pending on v1',
  v1_other: 'Billed on v1',
  v1_collected: 'Collected after the move',
};

/** The Session fields the label reads. A Site caller's row carries only
 *  status, transaction_id and cost; the rest are optional for that reason. */
export interface SessionPaymentFacts {
  status: string;
  transaction_id?: string | null;
  cost?: { total?: number | null } | null;
  stripe_payment_intent_id?: string | null;
  pre_auth_payment_intent_id?: string | null;
  capture_payment_intent_id?: string | null;
  card_charge_cents?: number | null;
  failure_reason?: string | null;
  payment_status?: string | null;
  stripe_receipt_url?: string | null;
  wallet_debited?: boolean | null;
  wallet_debit_cents?: number | null;
  manual_review_required?: boolean | null;
  mirrored_from_v1?: boolean | null;
  v1_payment_status?: string | null;
  /** Set when the 2026-10-23 flip clean-up collected the v1 session on v3's Stripe. */
  v1_cleanup?: { payment_intent?: string | null } | null;
  /** A Site caller's copy of the same fact: the Site allowlist carries no payment ids. */
  v1_collected?: boolean | null;
  /** An operations resolution of an uncollected session (scripts/recover-system-closed-uncollected.ts):
   *  'ops_waiver' means the driver owes nothing and EVI owes the host its share. */
  recovery_resolution?: { method?: string | null } | null;
}

const IN_FLIGHT = new Set(['active', 'pending', 'sweeping']);

/**
 * v1 history (2026-10-23): a mirrored session was billed by v1 and carries
 * no v3 payment fields, so v3's rule read every one "Pending". Warren: show
 * them as paid on v1, or v1's own status.
 */
function v1State(status: string | null | undefined): SessionPaymentState {
  const s = (status ?? '').toLowerCase();
  if (s === 'success' || s === 'paid' || s === 'succeeded') return 'v1_paid';
  if (s === 'failed') return 'v1_failed';
  if (s === 'pending' || s === 'processing') return 'v1_pending';
  return 'v1_other';
}

/** No real OCPP transaction: the shell placeholder or the legacy ''. */
export function sessionNeverStarted(s: Pick<SessionPaymentFacts, 'transaction_id'>): boolean {
  return !s.transaction_id || s.transaction_id.startsWith('pending-');
}

function moneyCollected(s: SessionPaymentFacts): boolean {
  // StopTransaction's held-principal capture records capture_payment_intent_id
  // without payment_status 'captured'; the overage leg records card_charge_cents.
  return s.payment_status === 'captured'
    || Boolean(s.capture_payment_intent_id)
    || Boolean(s.stripe_receipt_url)
    || (s.card_charge_cents ?? 0) > 0
    || s.wallet_debited === true
    || (s.wallet_debit_cents ?? 0) > 0;
}

export function sessionPaymentState(s: SessionPaymentFacts): SessionPaymentState {
  // A v1 session the flip clean-up collected on v3's Stripe reads collected
  // after the move, whatever v1 still says (no mirror pass writes v1_cleanup;
  // one may put v1's 'Failed' back).
  if (s.mirrored_from_v1 === true) {
    return s.v1_cleanup?.payment_intent || s.v1_collected === true ? 'v1_collected' : v1State(s.v1_payment_status);
  }
  if (IN_FLIGHT.has(s.status)) return 'live';
  const noCharge = !moneyCollected(s) && (sessionNeverStarted(s) || !((s.cost?.total ?? 0) > 0));
  // "Released" only on proof the hold was let go: StopTransaction's
  // 'cancelled_zero_cost'. A cancel that failed at Stripe leaves the same
  // fields as one that worked, so a refused start reads "No charge", which is
  // true either way.
  if (noCharge) return s.payment_status === 'cancelled_zero_cost' ? 'released' : 'no_charge';
  // The hold was captured but the overage above it failed (StopTransaction
  // PATH B and the stranded card leg write capture_payment_intent_id first,
  // then 'overage_failed'): the rest is still owed, so never "Paid". A later
  // successful overage collection writes 'captured' (sessionBalance.ts).
  // capture_failed is not checked here: a legacy row's later shortfall charge
  // records card_charge_cents and leaves capture_failed, and that one is paid.
  if (s.payment_status === 'overage_failed' && moneyCollected(s)) return 'partly_paid';
  if (moneyCollected(s)) return 'paid';
  // A billable session with nothing collected never reads "Paid" (SPARK2,
  // 28 Oct: the stranded closer cancelled the hold, and the cancel webhook
  // stamps stripe_payment_intent_id, which the old rule read as paid).
  // Warren, 28 Oct: the two uncollected stranded sessions were waived; EVI owes each host its share.
  if (s.recovery_resolution?.method === 'ops_waiver') return 'waived_by_evi';
  if (s.failure_reason === 'waived_below_minimum') return 'waived';
  if (s.payment_status === 'capture_failed' || s.payment_status === 'overage_failed') return 'failed';
  if (s.payment_status === 'held') return 'pending';
  if (s.payment_status === 'cancelled' || s.stripe_payment_intent_id || s.pre_auth_payment_intent_id) return 'not_collected';
  return 'pending';
}
